import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { InfoNotice } from '@/components/patterns/info-notice'
import { DatePickerControl } from '@/components/form/DatePicker'
import {
  AdminHeaderActionButton,
  AdminHeaderIconButton,
  AdminRecordHeader,
} from '@/components/patterns/admin-page'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import FulfillmentConfirmationSummary from '@/components/forms/Orders/FulfillmentConfirmationSummary'
import {
  RecipientDetailsForm,
  type RecipientDetailsValues,
} from '@/components/forms/Orders/EditRecipientDetailsForm'
import { OrderAside } from '@/components/orders/OrderAside'
import ScheduledFulfillmentWell from '@/components/orders/ScheduledFulfillmentWell'
import {
  getSubscriptionIssueTone,
  getSubscriptionName,
  hasSubscriptionPaymentProblem,
} from '@/components/subscriptions/subscription-display'
import { SubscriptionCreatedOrderNotice } from '@/components/subscriptions/SubscriptionCreatedOrderNotice'
import { getSubscriptionProblemNotice } from '@/components/subscriptions/subscription-problem-notice'
import { SubscriptionScheduleDialog } from '@/components/subscriptions/SubscriptionScheduleDialog'
import { SubscriptionFulfillmentDialog } from '@/components/subscriptions/SubscriptionFulfillmentDialog'
import {
  AcceptedTermsAsideCard,
  BillingAsideCard,
  NextCycleAsideCard,
  PersonalTouchesAsideCard,
  PurchaserAsideCard,
  SubscriptionPaymentMethodDialog,
  SubscriptionWorkspace,
} from '@/components/subscriptions/SubscriptionWorkspace'
import { getSubscriptionFulfillmentDateConfig } from '@/components/subscriptions/subscription-fulfillment'
import {
  subscriptionDestinationWellClass as destinationWellClass,
  subscriptionPaleIconClass as paleIconClass,
  subscriptionRecipientWellClass as recipientWellClass,
  subscriptionScheduledWellClass as scheduledWellClass,
  subscriptionSurfaceActionClass as surfaceActionClass,
} from '@/components/subscriptions/subscription-show-styles'
import { Badge, type BadgeVariant } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DetailIconBadge, DetailSectionSurface } from '@/components/patterns/detail-page'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Switch } from '@/components/ui/switch'
import { Typography } from '@/components/ui/typography'
import { formatPhone } from '@/hooks/use-phone-formatter'
import { Can, useAbility, type AppAbility } from '@/lib/casl'
import type {
  FlowerSubscription,
  FlowerSubscriptionActionResult,
} from '@/lib/services/flower-subscriptions/flower-subscriptions.types'
import dayjs from '@/lib/dayjs'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import {
  getPosFulfillmentDateAvailability,
  getPosFulfillmentDateBlockKind,
  getPosFulfillmentDateBlockTooltip,
  getPosFulfillmentDateWarning,
} from '@/lib/pos/fulfillment-availability'
import {
  flowerSubscriptionQueries,
  useCancelFlowerSubscriptionMutation,
  useFlowerSubscription,
  useResumeSkippedFlowerSubscriptionMutation,
  useSkipFlowerSubscriptionMutation,
  useUpdateFlowerSubscriptionFulfillmentDateMutation,
  useUpdateFlowerSubscriptionRecipientMutation,
} from '@/lib/services/flower-subscriptions/flower-subscriptions.query'
import {
  getSubscriptionAddressLines as getAddressLines,
  mapsUrlForSubscriptionAddress as mapsUrlForAddressLines,
  optionalSubscriptionText as optionalText,
} from '@/lib/services/flower-subscriptions/subscription-show'
import { cn, getErrorMessage, toYmdFromDate } from '@/lib/utils'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { getNextSubscriptionCycleDate } from '@/lib/services/flower-subscriptions/subscription-dates'
import {
  canUpdateSubscriptionPaymentMethod,
  getSubscriptionScheduleAction,
} from '@/lib/services/flower-subscriptions/subscription-action-state'
import {
  AlertTriangle,
  CalendarClock,
  CalendarDays,
  CreditCard,
  EllipsisVertical,
  GiftIcon,
  Mail,
  MapPin,
  PackageCheck,
  Pencil,
  Phone,
  Repeat2,
  RotateCcw,
  SendIcon,
  ShoppingCart,
  XCircle,
  XIcon,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

type SubscriptionProps = { subscription: FlowerSubscription }
type EditableSubscriptionProps = SubscriptionProps & { isReadOnly: boolean }
type ActionSubscriptionProps = SubscriptionProps & { surface?: 'header' | 'notice' }
type ScheduleSubscriptionProps = EditableSubscriptionProps & ActionSubscriptionProps
type SubscriptionScheduleMutation =
  | Pick<ReturnType<typeof useSkipFlowerSubscriptionMutation>, 'mutateAsync'>
  | Pick<ReturnType<typeof useResumeSkippedFlowerSubscriptionMutation>, 'mutateAsync'>

const subscriptionEditFields = ['details', 'payments', 'schedule', 'fulfillment', 'cancellation']

function canEditAnySubscriptionSection(ability: Pick<AppAbility, 'can'>): boolean {
  return subscriptionEditFields.some((field) => ability.can('edit', 'flower-subscriptions', field))
}

export const Route = createFileRoute('/_authenticated/$orgId/subscriptions/$flowerSubscriptionId')({
  component: RouteComponent,
  // Preserve the raw subscription ID alongside the parent's parsed organization ID.
  params: {
    parse: ({ flowerSubscriptionId }) => ({ flowerSubscriptionId }),
  },
  head: adminRouteTitleHead,
  loader: async ({ context, params }) => {
    if (!context.ability.can('view', 'flower-subscriptions')) throw notFound()

    const subscription = await context.queryClient.ensureQueryData(
      flowerSubscriptionQueries.show({
        organizationId: params.orgId,
        flowerSubscriptionId: params.flowerSubscriptionId,
      })
    )

    return {
      subscription,
      isReadOnly: !canEditAnySubscriptionSection(context.ability),
    }
  },
})

function showFulfillmentDateUpdateOutcome(response: FlowerSubscriptionActionResult): void {
  const subscription = response?.data
  const status = String(subscription?.status || '').toLowerCase()
  const issueType = String(subscription?.issue_type || '').toLowerCase()

  if (status === 'payment_failed') {
    toast.error('Payment failed', {
      description:
        subscription?.last_error ||
        'Update the payment method to keep the upcoming order on schedule.',
    })
    return
  }

  if (status === 'requires_review' && issueType === 'payment_review') {
    toast.error('Payment needs review', {
      description:
        subscription?.last_error || 'The date was updated, but the payment still needs attention.',
    })
    return
  }

  if (status === 'requires_review') {
    toast.error('Upcoming order needs attention', {
      description:
        subscription?.last_error ||
        'The date was updated, but the upcoming order still needs attention.',
    })
    return
  }

  toast.success('Current order date updated')
}

function SubscriptionHeaderStatusBadge({ subscription }: SubscriptionProps) {
  const status = subscription?.status
  const label = subscription?.status_label || 'Subscription'
  const variant =
    status === 'requires_review'
      ? getSubscriptionIssueTone(subscription)
      : (
          {
            active: 'success',
            payment_failed: 'destructive',
            cancelled: 'muted',
          } satisfies Record<Exclude<FlowerSubscription['status'], 'requires_review'>, BadgeVariant>
        )[status] || 'secondary'

  return <Badge variant={variant}>{label}</Badge>
}

function SubscriptionHeaderSkippedBadge({ subscription }: SubscriptionProps) {
  if (!subscription?.has_skipped_next_cycle || subscription?.status === 'cancelled') {
    return null
  }

  return (
    <Badge variant="warning" size="sm">
      <CalendarClock className="size-3" />
      Next order skipped
    </Badge>
  )
}

function ProblemBanner({ subscription, isReadOnly }: EditableSubscriptionProps) {
  const ability = useAbility()

  if (!hasSubscriptionPaymentProblem(subscription)) {
    return null
  }

  const notice = getSubscriptionProblemNotice(subscription)

  if (!notice) {
    return null
  }

  const Icon = notice.icon
  const isPaymentDanger = ['payment_failed', 'payment_review'].includes(notice.issueType)
  const isPaymentIssue = ['payment_failed', 'payment_review'].includes(notice.issueType)
  const canManagePayments =
    !isReadOnly &&
    subscription.status !== 'cancelled' &&
    isPaymentIssue &&
    ability.can('edit', 'flower-subscriptions', 'payments')
  const canSkipCurrentOrder =
    !isReadOnly &&
    subscription.status !== 'cancelled' &&
    subscription.can_skip_current_order &&
    ability.can('edit', 'flower-subscriptions', 'fulfillment')
  const canManageFulfillment =
    !isReadOnly &&
    subscription.status !== 'cancelled' &&
    notice.issueType === 'fulfillment_unavailable' &&
    ability.can('edit', 'flower-subscriptions', 'fulfillment')
  const action =
    isPaymentIssue && (canManagePayments || canSkipCurrentOrder) ? (
      <>
        {canManagePayments ? (
          <SubscriptionPaymentMethodDialog subscription={subscription} surface="notice" />
        ) : null}
        {canSkipCurrentOrder ? (
          <SubscriptionSkipCurrentCycleDialog subscription={subscription} surface="notice" />
        ) : null}
      </>
    ) : canManageFulfillment ? (
      <SubscriptionFulfillmentReviewActions subscription={subscription} surface="notice" />
    ) : null

  return (
    <InfoNotice
      title={notice.title}
      tone={isPaymentDanger ? 'danger' : 'warning'}
      icon={<Icon />}
      action={
        action ? (
          <div className="flex max-w-full flex-wrap items-center justify-start gap-2 sm:justify-end">
            {action}
          </div>
        ) : null
      }
    >
      {notice.description}
    </InfoNotice>
  )
}

function SubscriptionFulfillmentDateDialog({
  subscription,
  surface = 'header',
}: ActionSubscriptionProps) {
  const [open, setOpen] = useState(false)
  const [fulfillmentDate, setFulfillmentDate] = useState('')
  const [allowUnavailable, setAllowUnavailable] = useState(false)
  const updateFulfillmentDateMutation = useUpdateFlowerSubscriptionFulfillmentDateMutation()
  const isSaving = updateFulfillmentDateMutation.isPending
  const fulfillment = subscription.fulfillment ?? {}
  const {
    isPickup,
    label: fulfillmentDateLabel,
    usesLocationAvailability,
  } = getSubscriptionFulfillmentDateConfig(fulfillment.type, 'New')
  const fulfillmentLocation = fulfillment.location ?? null
  const maxFulfillmentDate = getNextSubscriptionCycleDate(subscription)?.subtract(1, 'day')
  const fulfillmentDateAvailability = useMemo(() => {
    if (!fulfillmentLocation || !usesLocationAvailability) {
      return null
    }

    return getPosFulfillmentDateAvailability(fulfillmentLocation, {
      isPickup,
      useHoursFallbackForMissingWindows: true,
    })
  }, [fulfillmentLocation, isPickup, usesLocationAvailability])
  const dateWarning = getPosFulfillmentDateWarning(fulfillmentDateAvailability, fulfillmentDate, {
    isPickup,
    orderLabel: 'this subscription order',
  })
  const canSaveFulfillmentDate =
    Boolean(fulfillmentDate) && !isSaving && (!dateWarning || allowUnavailable)

  const openDialog = () => {
    setFulfillmentDate('')
    setAllowUnavailable(false)
    setOpen(true)
  }

  const saveFulfillmentDate = async () => {
    if (!fulfillmentDate) {
      return
    }

    try {
      const response = await updateFulfillmentDateMutation.mutateAsync({
        flowerSubscriptionId: subscription.id,
        fulfillmentDate,
        allowUnavailable: Boolean(dateWarning && allowUnavailable),
      })
      showFulfillmentDateUpdateOutcome(response)
      setOpen(false)
    } catch (error) {
      toast.error('Date update failed', {
        description: getErrorMessage(error),
      })
    }
  }

  const trigger =
    surface === 'notice' ? (
      <Button
        variant="outlineCard"
        size="header"
        onClick={openDialog}
        disabled={isSaving || subscription.status === 'cancelled'}
      >
        <CalendarDays className="size-4" />
        Change Upcoming Date
      </Button>
    ) : (
      <AdminHeaderActionButton
        type="button"
        variant="outline"
        onClick={openDialog}
        disabled={isSaving || subscription.status === 'cancelled'}
      >
        <CalendarDays className="size-4" />
        Change Upcoming Date
      </AdminHeaderActionButton>
    )

  return (
    <>
      {trigger}

      <Dialog open={open} onOpenChange={(nextOpen) => !isSaving && setOpen(nextOpen)}>
        <DialogContent size="small">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2">
              <CalendarDays className="size-4 shrink-0" />
              Change upcoming order date
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-5">
            <div className="rounded-md border border-primary/20 bg-primary/5 p-3">
              <Typography variant="label">Choose a new date for the order cycle.</Typography>
              <div className="mt-2 grid gap-2 text-sm leading-5 text-muted-foreground">
                <span className="flex items-start gap-2">
                  <CalendarClock className="mt-0.5 size-4 shrink-0 text-success" />
                  Only this upcoming order will move
                </span>
                <span className="flex items-start gap-2">
                  <Repeat2 className="mt-0.5 size-4 shrink-0 text-interaction" />
                  The subscription continues on its regular schedule afterward
                </span>
                <span className="flex items-start gap-2">
                  <CreditCard className="mt-0.5 size-4 shrink-0 text-interaction" />
                  Updating the date will retry payment for this order immediately
                </span>
              </div>
            </div>

            <DatePickerControl
              label={fulfillmentDateLabel}
              value={fulfillmentDate}
              onChange={(nextDate) => {
                setFulfillmentDate(nextDate)
                setAllowUnavailable(false)
              }}
              minDate={dayjs().toDate()}
              maxDate={maxFulfillmentDate?.isValid() ? maxFulfillmentDate.toDate() : null}
              preventOpenAutoFocus
              disabled={(date) =>
                fulfillmentDateAvailability?.hardDisabledDates?.has(toYmdFromDate(date)) ?? false
              }
              modifiers={{
                availabilityClosed: (date) =>
                  getPosFulfillmentDateBlockKind(
                    fulfillmentDateAvailability,
                    toYmdFromDate(date)
                  ) === 'closed',
                availabilityUnavailable: (date) =>
                  getPosFulfillmentDateBlockKind(
                    fulfillmentDateAvailability,
                    toYmdFromDate(date)
                  ) === 'unavailable',
              }}
              availabilityBlockLabel={(date) =>
                getPosFulfillmentDateBlockTooltip(
                  fulfillmentDateAvailability,
                  toYmdFromDate(date),
                  { isPickup }
                )
              }
              format="MMM D, YYYY"
              placeholder="Choose date"
              className="w-full"
            />

            {dateWarning ? (
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{dateWarning.title}</AlertTitle>
                <AlertDescription>{dateWarning.description}</AlertDescription>
              </Alert>
            ) : null}

            {dateWarning ? (
              <label className="flex items-start gap-3 rounded-lg border border-border/70 bg-muted/20 p-3 text-sm leading-5">
                <Checkbox
                  checked={allowUnavailable}
                  onCheckedChange={(checked) => setAllowUnavailable(Boolean(checked))}
                  className="mt-0.5"
                />
                <span>I can fulfill this order on the selected date.</span>
              </label>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="button" onClick={saveFulfillmentDate} disabled={!canSaveFulfillmentDate}>
              {isSaving ? 'Saving...' : 'Update Upcoming Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function SubscriptionSkipCurrentCycleDialog({
  subscription,
  surface = 'header',
}: ActionSubscriptionProps) {
  const skipMutation = useSkipFlowerSubscriptionMutation()
  const [open, setOpen] = useState(false)
  const [notifyCustomer, setNotifyCustomer] = useState(true)
  const isSkipping = skipMutation.isPending
  const canNotifyCustomer = Boolean(subscription.customer?.email)

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSkipping) {
      return
    }

    setOpen(nextOpen)

    if (nextOpen) {
      setNotifyCustomer(canNotifyCustomer)
    }
  }

  const skipCurrentCycle = async () => {
    try {
      await skipMutation.mutateAsync({
        flowerSubscriptionId: subscription.id,
        notifyCustomer: notifyCustomer && canNotifyCustomer,
      })
      toast.success(
        notifyCustomer && canNotifyCustomer
          ? 'Current order skipped and customer notified'
          : 'Current order skipped'
      )
      setOpen(false)
    } catch (error) {
      toast.error('Subscription update failed', {
        description: getErrorMessage(error),
      })
    }
  }

  const trigger =
    surface === 'notice' ? (
      <Button
        variant="outlineCard"
        size="header"
        disabled={
          isSkipping || subscription.status === 'cancelled' || !subscription.can_skip_current_order
        }
        onClick={() => handleOpenChange(true)}
      >
        <CalendarClock className="size-4" />
        Skip Current Order
      </Button>
    ) : (
      <AdminHeaderActionButton
        type="button"
        variant="outline"
        disabled={
          isSkipping || subscription.status === 'cancelled' || !subscription.can_skip_current_order
        }
        onClick={() => handleOpenChange(true)}
      >
        <CalendarClock className="size-4" />
        Skip Current Order
      </AdminHeaderActionButton>
    )

  return (
    <>
      {trigger}

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent size="small">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-balance">
              <CalendarClock className="size-4 shrink-0" />
              Skip current order?
            </DialogTitle>
            <DialogDescription className="sr-only">
              Skip the current subscription order and optionally notify the customer.
            </DialogDescription>
          </DialogHeader>

          <FulfillmentConfirmationSummary
            statusLabel="Skipped"
            statusText="Skip this scheduled order"
            icon={<CalendarClock className="size-4" />}
            notifyCustomer={notifyCustomer}
            notificationLabel={
              canNotifyCustomer
                ? notifyCustomer
                  ? 'Notify the customer'
                  : 'Will not notify the customer'
                : 'No purchaser email on file'
            }
            onNotifyCustomerChange={setNotifyCustomer}
            disabled={isSkipping || !canNotifyCustomer}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isSkipping}
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="button" disabled={isSkipping} onClick={skipCurrentCycle}>
              {isSkipping ? 'Skipping...' : 'Skip Current Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function SubscriptionSkipDetails({ mode = 'next' }: { mode?: 'next' | 'current' }) {
  const isCurrentCycle = mode === 'current'

  return (
    <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-left">
      <Typography variant="label">
        {isCurrentCycle
          ? 'This only skips the current subscription order.'
          : 'This only skips the next subscription order.'}
      </Typography>
      <div className="mt-2 grid gap-2 text-sm text-muted-foreground">
        <span className="flex items-start gap-2">
          <PackageCheck className="size-4 shrink-0 text-interaction" />
          {isCurrentCycle
            ? 'No order will be created for this scheduled date'
            : 'No order will be created for the upcoming order'}
        </span>
        <span className="flex items-start gap-2">
          <CreditCard className="size-4 shrink-0 text-interaction" />
          No payment will be collected for the skipped order
        </span>
        <span className="flex items-start gap-2">
          <Repeat2 className="size-4 shrink-0 text-interaction" />
          The subscription stays active and continues on its regular schedule afterward
        </span>
      </div>
    </div>
  )
}

function SubscriptionFulfillmentReviewActions({
  subscription,
  surface = 'header',
}: ActionSubscriptionProps) {
  return (
    <Can I="edit" a="flower-subscriptions" field="fulfillment">
      <div
        className={cn('flex flex-wrap items-center gap-2', surface === 'notice' && 'justify-end')}
      >
        <SubscriptionFulfillmentDateDialog subscription={subscription} surface={surface} />
        <SubscriptionSkipCurrentCycleDialog subscription={subscription} surface={surface} />
      </div>
    </Can>
  )
}

function SubscriptionScheduleActions({
  subscription,
  isReadOnly,
  surface = 'header',
}: ScheduleSubscriptionProps) {
  const skipMutation = useSkipFlowerSubscriptionMutation()
  const resumeMutation = useResumeSkippedFlowerSubscriptionMutation()
  const isCancelled = subscription.status === 'cancelled'
  const isPending = skipMutation.isPending || resumeMutation.isPending
  const scheduleAction = getSubscriptionScheduleAction(subscription)

  const runAction = async (mutation: SubscriptionScheduleMutation, successMessage: string) => {
    try {
      await mutation.mutateAsync(subscription.id)
      toast.success(successMessage)
    } catch (error) {
      toast.error('Subscription update failed', {
        description: getErrorMessage(error),
      })
    }
  }

  if (isReadOnly || isCancelled) {
    return null
  }

  if (scheduleAction === 'skip-current') {
    return (
      <Can I="edit" a="flower-subscriptions" field="fulfillment">
        <SubscriptionSkipCurrentCycleDialog subscription={subscription} surface={surface} />
      </Can>
    )
  }

  const resumeTrigger =
    surface === 'notice' ? (
      <Button variant="outlineCard" size="header" disabled={isPending}>
        <RotateCcw className="size-4" />
        Resume Regular Schedule
      </Button>
    ) : (
      <AdminHeaderActionButton type="button" variant="outline" disabled={isPending}>
        <RotateCcw className="size-4" />
        Resume Regular Schedule
      </AdminHeaderActionButton>
    )
  const skipTrigger =
    surface === 'notice' ? (
      <Button variant="outlineCard" size="header" disabled={isPending}>
        <CalendarClock className="size-4" />
        Skip Next Order
      </Button>
    ) : (
      <AdminHeaderActionButton type="button" variant="outline" disabled={isPending}>
        <CalendarClock className="size-4" />
        Skip Next Order
      </AdminHeaderActionButton>
    )

  return (
    <Can I="edit" a="flower-subscriptions" field="fulfillment">
      {scheduleAction === 'resume' ? (
        <ConfirmationDialog
          key="resume-skip"
          title="Resume regular schedule?"
          confirmText="Resume Regular Schedule"
          pendingText="Resuming..."
          isPending={resumeMutation.isPending}
          onConfirm={() => runAction(resumeMutation, 'Regular schedule resumed')}
          trigger={resumeTrigger}
          icon={<RotateCcw className="size-4" />}
        >
          <SubscriptionResumeDetails />
        </ConfirmationDialog>
      ) : scheduleAction === 'skip-next' ? (
        <ConfirmationDialog
          key="skip-next"
          title="Skip next order?"
          confirmText="Skip Next Order"
          cancelText="Cancel"
          pendingText="Skipping..."
          isPending={skipMutation.isPending}
          onConfirm={() => runAction(skipMutation, 'Next order skipped')}
          trigger={skipTrigger}
          icon={<CalendarClock className="size-4" />}
        >
          <SubscriptionSkipDetails />
        </ConfirmationDialog>
      ) : null}
    </Can>
  )
}

function SubscriptionResumeDetails() {
  return (
    <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-left">
      <Typography variant="label">
        This puts the skipped subscription order back on the regular schedule.
      </Typography>
      <div className="mt-2 grid gap-2 text-sm text-muted-foreground">
        <span className="flex items-start gap-2">
          <PackageCheck className="size-4 shrink-0 text-interaction" />
          The upcoming order will be created as scheduled
        </span>
        <span className="flex items-start gap-2">
          <CreditCard className="size-4 shrink-0 text-interaction" />
          Payment will be collected on the regular charge date
        </span>
        <span className="flex items-start gap-2">
          <Repeat2 className="size-4 shrink-0 text-interaction" />
          The subscription continues on its regular schedule afterward
        </span>
      </div>
    </div>
  )
}

function SubscriptionOverflowActions({ subscription, isReadOnly }: EditableSubscriptionProps) {
  const cancelMutation = useCancelFlowerSubscriptionMutation()
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const [notifyCustomer, setNotifyCustomer] = useState(true)
  const isCancelled = subscription.status === 'cancelled'

  const cancelSubscription = async () => {
    try {
      await cancelMutation.mutateAsync({
        flowerSubscriptionId: subscription.id,
        notifyCustomer,
      })
      toast.success(
        notifyCustomer ? 'Subscription cancelled and customer notified' : 'Subscription cancelled'
      )
      setIsCancelDialogOpen(false)
    } catch (error) {
      toast.error('Subscription update failed', {
        description: getErrorMessage(error),
      })
    }
  }

  const handleCancelDialogOpenChange = (nextOpen: boolean) => {
    if (!cancelMutation.isPending) {
      setIsCancelDialogOpen(nextOpen)

      if (nextOpen) {
        setNotifyCustomer(true)
      }
    }
  }

  if (isReadOnly || isCancelled || !subscription.can_cancel) {
    return null
  }

  return (
    <Can I="edit" a="flower-subscriptions" field="cancellation">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <AdminHeaderIconButton
            icon={EllipsisVertical}
            label="More subscription actions"
            disabled={cancelMutation.isPending}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" size="md">
          <DropdownMenuGroup>
            <DropdownMenuItem
              variant="destructive"
              disabled={cancelMutation.isPending}
              onSelect={() => handleCancelDialogOpenChange(true)}
            >
              <XCircle className="size-4" />
              Cancel Subscription
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={isCancelDialogOpen} onOpenChange={handleCancelDialogOpenChange}>
        <DialogContent size="small">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-balance">
              <AlertTriangle className="size-4 shrink-0 text-destructive" />
              Cancel subscription?
            </DialogTitle>
            <DialogDescription className="sr-only">
              Canceling this subscription cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border border-border/70 bg-muted/20 p-3">
              <Typography variant="label">This action cannot be undone.</Typography>
              <div className="mt-2 grid gap-2 text-sm text-muted-foreground">
                <span className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2">
                    {notifyCustomer ? (
                      <SendIcon className="size-4 shrink-0 text-interaction" />
                    ) : (
                      <XCircle className="size-4 shrink-0 text-destructive" />
                    )}
                    {notifyCustomer ? 'Notify the customer' : 'Will not notify the customer'}
                  </span>
                  <Switch
                    checked={notifyCustomer}
                    disabled={cancelMutation.isPending}
                    aria-label="Notify the customer"
                    onCheckedChange={setNotifyCustomer}
                  />
                </span>
                <span className="flex items-center gap-2">
                  <CalendarClock className="size-4 shrink-0 text-muted-foreground" /> Stop future
                  charges and scheduled orders
                </span>
                <span className="flex items-center gap-2">
                  <XIcon className="size-4 shrink-0 text-destructive" /> Cancel this subscription
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={cancelMutation.isPending}
              onClick={() => handleCancelDialogOpenChange(false)}
            >
              Keep Subscription
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={cancelMutation.isPending}
              onClick={cancelSubscription}
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Can>
  )
}

function SubscriptionHeaderContactGrid({
  subscription,
  canEditRecipient,
  onEditRecipient,
}: SubscriptionProps & { canEditRecipient: boolean; onEditRecipient: () => void }) {
  const fulfillment = subscription.fulfillment ?? {}
  const deliveryAddressLines = getAddressLines(fulfillment.delivery_address)
  const shippingAddressLines = getAddressLines(fulfillment.shipping_address)
  const pickupAddressLines = getAddressLines(fulfillment.location_address)
  const addressLines =
    fulfillment.type === 'delivery'
      ? deliveryAddressLines
      : fulfillment.type === 'shipping'
        ? shippingAddressLines
        : pickupAddressLines
  const mapsUrl = mapsUrlForAddressLines(addressLines)
  const isPickup = fulfillment.type === 'pickup' || fulfillment.type === 'in-store'
  const FulfillmentIcon = isPickup
    ? ShoppingCart
    : fulfillment.type === 'shipping'
      ? PackageCheck
      : MapPin

  return (
    <div
      data-testid="subscription-contact-grid"
      className="grid gap-px bg-border/45 lg:grid-cols-2"
    >
      <DetailSectionSurface tone="amethyst">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <DetailIconBadge icon={GiftIcon} tone="amethyst" />
            <div className="min-w-0">
              <Typography variant="eyebrow">Recipient</Typography>
              <Typography variant="cardTitle" className="mt-2 pb-0">
                {fulfillment.recipient_name || 'No recipient name provided'}
              </Typography>
            </div>
          </div>
          {canEditRecipient ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className={surfaceActionClass}
              onClick={onEditRecipient}
              aria-label="Edit recipient details"
            >
              <Pencil className="size-4" />
            </Button>
          ) : null}
        </div>

        <div className="space-y-2.5">
          <div className={cn('flex items-center gap-3', recipientWellClass)}>
            <Phone className={cn(paleIconClass, 'stroke-interaction')} />
            <div className="min-w-0 flex-1">
              <span className="sr-only">Recipient phone</span>
              <span className="block truncate text-sm">
                {fulfillment.recipient_phone
                  ? formatPhone(fulfillment.recipient_phone)
                  : '(None provided)'}
              </span>
            </div>
          </div>

          <div className={cn('flex items-center gap-3', recipientWellClass)}>
            <Mail className={cn(paleIconClass, 'stroke-interaction')} />
            <div className="min-w-0 flex-1">
              <span className="sr-only">Recipient email</span>
              <span className="block truncate text-sm">
                {optionalText(fulfillment.recipient_email)}
              </span>
            </div>
          </div>
        </div>
      </DetailSectionSurface>

      <DetailSectionSurface tone="periwinkle">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <DetailIconBadge icon={FulfillmentIcon} tone="brand" />
            <div className="min-w-0">
              <Typography variant="eyebrow">
                {fulfillment.type_label || (isPickup ? 'Pickup location' : 'Delivery')}
              </Typography>
            </div>
          </div>
          <SubscriptionFulfillmentDialog
            subscription={subscription}
            triggerClassName={surfaceActionClass}
          />
        </div>

        <div className="space-y-2.5">
          <ScheduledFulfillmentWell
            fulfillmentType={fulfillment.type}
            fulfillmentDate={subscription.next_fulfillment_date}
            isCancelled={subscription.status === 'cancelled'}
            compactWellClassName={scheduledWellClass}
          />

          <div className={destinationWellClass}>
            <div className="flex items-center gap-3">
              <MapPin className={cn(paleIconClass, 'self-center stroke-interaction')} />
              <div className="min-w-0 flex-1 text-sm/6">
                {addressLines.length > 0 && mapsUrl ? (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="min-w-0 underline-offset-4 hover:underline"
                  >
                    {addressLines.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </a>
                ) : addressLines.length > 0 ? (
                  <div className="min-w-0 text-foreground">
                    {addressLines.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">
                    No destination information has been added yet.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </DetailSectionSurface>
    </div>
  )
}

function RouteComponent() {
  const { orgId, flowerSubscriptionId } = Route.useParams()
  const { isReadOnly } = Route.useLoaderData()
  const ability = useAbility()
  const updateRecipientMutation = useUpdateFlowerSubscriptionRecipientMutation()
  const [isRecipientAsideOpen, setIsRecipientAsideOpen] = useState(false)
  const { data: subscription } = useFlowerSubscription(flowerSubscriptionId, {
    organizationId: orgId,
  })
  const headerMetaItems = [
    `Started ${subscription.created_at ? dayjs(subscription.created_at).format('MMM D, YYYY') : '-'}`,
  ]
  const showProblemBanner = hasSubscriptionPaymentProblem(subscription)
  const canUpdatePayment = canUpdateSubscriptionPaymentMethod({
    ability,
    isReadOnly,
    subscription,
  })
  const scheduleAction = getSubscriptionScheduleAction(subscription)
  const canChangeSchedule =
    !isReadOnly && subscription.status !== 'cancelled' && Boolean(scheduleAction)
  const canCancelSubscription =
    !isReadOnly && subscription.status !== 'cancelled' && subscription.can_cancel
  const canEditRecipient =
    !isReadOnly &&
    subscription.status !== 'cancelled' &&
    ability.can('edit', 'flower-subscriptions', 'details')
  const showHeaderPaymentAction = canUpdatePayment
  const showHeaderScheduleAction = canChangeSchedule
  const showSecondaryActions = showHeaderPaymentAction || showHeaderScheduleAction

  return (
    <div className="-mr-4 mb-10">
      <div className="space-y-4 pr-6 pt-1">
        {showProblemBanner ? (
          <div>
            <ProblemBanner subscription={subscription} isReadOnly={isReadOnly} />
          </div>
        ) : null}
        <SubscriptionCreatedOrderNotice order={subscription.created_upcoming_order} />

        <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)] xl:items-start">
          <div className="contents xl:col-start-1 xl:row-start-1 xl:block xl:min-w-0 xl:space-y-5">
            <div className="order-1 min-w-0">
              <AdminRecordHeader
                icon={Repeat2}
                iconTone={hasSubscriptionPaymentProblem(subscription) ? 'warning' : 'mint'}
                title={getSubscriptionName(subscription)}
                layout="workspace"
                titleTrailing={
                  <>
                    <SubscriptionHeaderStatusBadge subscription={subscription} />
                    <SubscriptionHeaderSkippedBadge subscription={subscription} />
                  </>
                }
                meta={
                  <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {headerMetaItems.map((item) => (
                      <Typography variant="supporting" key={item}>
                        {item}
                      </Typography>
                    ))}
                  </div>
                }
                actions={
                  canCancelSubscription ? (
                    <SubscriptionOverflowActions
                      subscription={subscription}
                      isReadOnly={isReadOnly}
                    />
                  ) : null
                }
                secondaryActions={
                  showSecondaryActions ? (
                    <>
                      {showHeaderPaymentAction ? (
                        <SubscriptionPaymentMethodDialog
                          subscription={subscription}
                          surface="header"
                        />
                      ) : null}
                      {showHeaderScheduleAction ? (
                        <SubscriptionScheduleActions
                          subscription={subscription}
                          isReadOnly={isReadOnly}
                        />
                      ) : null}
                    </>
                  ) : null
                }
                after={
                  <SubscriptionHeaderContactGrid
                    subscription={subscription}
                    canEditRecipient={canEditRecipient}
                    onEditRecipient={() => setIsRecipientAsideOpen(true)}
                  />
                }
              />
            </div>

            <div className="order-3 min-w-0">
              <SubscriptionWorkspace subscription={subscription} />
            </div>
          </div>

          <div className="order-2 grid min-w-0 gap-4 lg:grid-cols-2 xl:col-start-2 xl:row-start-1 xl:order-none xl:block xl:self-start xl:gap-0 xl:space-y-4 xl:sticky xl:top-1">
            <PurchaserAsideCard subscription={subscription} />
            <NextCycleAsideCard subscription={subscription} />
            <BillingAsideCard subscription={subscription} isReadOnly={isReadOnly} />
            <PersonalTouchesAsideCard subscription={subscription} isReadOnly={isReadOnly} />
            <AcceptedTermsAsideCard subscription={subscription} />
          </div>
        </div>
      </div>

      <OrderAside
        open={isRecipientAsideOpen}
        onOpenChange={setIsRecipientAsideOpen}
        title="Edit Subscription Recipient"
        description="Update the recipient details used for future subscription orders"
      >
        <RecipientDetailsForm
          initialData={{
            id: subscription.id,
            recipient_name: subscription.fulfillment?.recipient_name,
            recipient_phone: subscription.fulfillment?.recipient_phone,
            recipient_email: subscription.fulfillment?.recipient_email,
          }}
          closeAside={() => setIsRecipientAsideOpen(false)}
          isPending={updateRecipientMutation.isPending}
          subtitle="Update who should receive future subscription orders and how your team can reach them."
          onSave={(recipient: RecipientDetailsValues) =>
            updateRecipientMutation.mutateAsync({
              flowerSubscriptionId: subscription.id,
              recipientName: recipient.recipient_name,
              recipientPhone: recipient.recipient_phone,
              recipientEmail: recipient.recipient_email,
            })
          }
        />
      </OrderAside>
    </div>
  )
}
